import { useEffect } from "react";
import { syncPolygonLayer, setPolygonVisibility } from "../../utils/polygonLayers";
import { syncImpactLayers, setImpactLayersVisibility } from "../../utils/impactLayers";
import { syncViewedLayers, setViewedLayersVisibility } from "../../utils/viewedlayers.js";
import { applyLayerOrder } from "../../utils/MapLayerUtils";
import { syncRelief } from "../../utils/Relief3d.js";

// Draws the app's data on the map. Every effect re-runs when styleVersion
// changes, because a new basemap style removes all custom layers.
const useMapSync = ({
  mapRef,
  mapReady,
  styleVersion,
  styleLoading,
  realPolygon,
  simPolygon,
  impacts,
  viewed,
  relief,
  impactVisible,
  impactVisibleRef,
}) => {
  /* ------------------- CURRENT analysis: impact results ------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || styleLoading.current) return;
    syncImpactLayers(map, impacts?.data, impactVisibleRef.current);
    setImpactLayersVisibility(map, impactVisibleRef.current);
    // the polygon features are not used here: check whether they are needed
    // in this list (e.g. to clear impacts when a polygon changes)
  }, [mapReady, styleVersion, impacts?.data, realPolygon.feature, simPolygon.feature]);

  /* --------- HISTORICAL analysis: its own layers, fully independent -------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || styleLoading.current) return;
    syncViewedLayers(map, viewed?.data);
    // new layers must respect the current legend toggles + stacking order
    setViewedLayersVisibility(map, impactVisibleRef.current);
    applyLayerOrder(map);
  }, [mapReady, styleVersion, viewed?.data]);

  /* ------------- CURRENT analysis: display polygons on the map ------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || styleLoading.current) return;
    syncPolygonLayer(map, "real", realPolygon.feature);
    syncPolygonLayer(map, "sim", simPolygon.feature);
    setPolygonVisibility(map, "real", impactVisibleRef.current.real);
    setPolygonVisibility(map, "sim", impactVisibleRef.current.sim);
    // polygons are added after the impact layers, so restore the stacking order
    applyLayerOrder(map);
  }, [mapReady, styleVersion, realPolygon.feature, simPolygon.feature]);

  /* ------------------------------- legend toggles ------------------------------- */
  // the same toggles drive the current AND the historic analysis layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || styleLoading.current) return;
    setImpactLayersVisibility(map, impactVisible);
    setViewedLayersVisibility(map, impactVisible);
    setPolygonVisibility(map, "real", impactVisible.real);
    setPolygonVisibility(map, "sim", impactVisible.sim);
  }, [impactVisible, mapReady]);

  /* -------------------------------- relief ------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || styleLoading.current) return;
    syncRelief(map, relief);
  }, [mapReady, styleVersion, relief]);
};

export default useMapSync;
